import { getAccessToken } from "@/auth/getAccessToken";
import { runStaffCommand, StaffCommandNotSentError } from "@/services/staffCommands";
import { extractApiError } from "@/lib/api-errors";
import { captureStaffEpoch, assertStaffEpoch, observeStaffRead, assertStaffWriteAvailable } from "@/services/staffTransportSession";

// Build-time replaced by Bun's define plugin; in dev (HMR) this is undefined
declare const __API_BASE_URL__: string | undefined;
const API_BASE_URL = typeof __API_BASE_URL__ !== "undefined" ? __API_BASE_URL__ : "";
const responseEpochs = new WeakMap<Response, number>();
function assertResponseCurrent(response: Response) {
  const epoch = responseEpochs.get(response);
  if (epoch !== undefined) assertStaffEpoch(epoch);
}
async function readJson<T>(response: Response): Promise<T> {
  const value = await response.json();
  assertResponseCurrent(response);
  return value as T;
}

/**
 * An HTTP failure that kept its status code and body. Callers need the status to tell a
 * stale concurrency token (409 — reload and retry) from a rejected value (422 — show the
 * message on the field), and the body to place per-field validation errors.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }

  /** ValidationProblemDetails errors, keyed the way the server keyed them (`income[0].amounts`). */
  get fieldErrors(): Record<string, string[]> {
    const body = this.body as { errors?: Record<string, unknown> } | null;
    if (!body || typeof body.errors !== "object" || body.errors === null) return {};
    return Object.fromEntries(
      Object.entries(body.errors).map(([field, messages]) => [
        field,
        (Array.isArray(messages) ? messages : [messages]).filter(
          (message): message is string => typeof message === "string",
        ),
      ]),
    );
  }
}

async function failure(response: Response, method: string, path: string): Promise<ApiError> {
  let body: unknown = null;
  let detail = "";
  try {
    body = await response.json();
    detail = extractApiError(body);
  } catch {
    // A non-JSON body carries nothing useful beyond the status code.
  }
  assertResponseCurrent(response);
  return new ApiError(
    response.status,
    detail || `${method} ${path} failed: ${response.status}`,
    body,
  );
}

export async function apiClient(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const epoch = captureStaffEpoch();
  const isRead = ["GET", "HEAD"].includes((options.method ?? "GET").toUpperCase());
  if (!isRead) assertStaffWriteAvailable(path);
  let token: string;
  try { token = await getAccessToken(); }
  catch (error) {
    if (!isRead) throw new StaffCommandNotSentError("authentication-unavailable");
    throw error;
  }
  assertStaffEpoch(epoch);
  if (!isRead) assertStaffWriteAvailable(path);
  const read = isRead ? observeStaffRead() : null;
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      signal: read ? (options.signal ? AbortSignal.any([read.signal, options.signal]) : read.signal) : options.signal,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        // Protocol acknowledgement, never an authorization grant or legacy adoption.
        "X-CMS-Workflow-Protocol": "2",
        ...options.headers,
      },
    });
    assertStaffEpoch(epoch);
    responseEpochs.set(response, epoch);
    return response;
  } finally { read?.release(); }
}

// Seal the endpoint builder's exact body before authentication or network work. Parsing and
// HTTP errors belong to the same observed attempt, not a second request/retry layer.
function writeJson<T>(path: string, method: string, body: string | undefined): Promise<T> {
  return runStaffCommand({ path, method, body }, async request => {
    const response = await apiClient(request.path, { method: request.method, body: request.body });
    if (!response.ok) throw await failure(response, request.method, request.path);
    return response.status === 204 ? undefined as T : readJson<T>(response);
  });
}

// Convenience methods
export const api = {
  async get<T>(path: string): Promise<T> {
    const response = await apiClient(path);
    if (!response.ok) {
      throw await failure(response, "GET", path);
    }
    return readJson<T>(response);
  },

  async post<T>(path: string, body?: unknown): Promise<T> {
    return writeJson<T>(path, "POST", body ? JSON.stringify(body) : undefined);
  },

  async patch<T>(path: string, body: unknown): Promise<T> {
    return writeJson<T>(path, "PATCH", JSON.stringify(body));
  },

  async put<T>(path: string, body: unknown): Promise<T> {
    return writeJson<T>(path, "PUT", JSON.stringify(body));
  },

  async del<T>(path: string, body?: unknown): Promise<T> {
    return writeJson<T>(path, "DELETE", body === undefined ? undefined : JSON.stringify(body));
  },

  // For file uploads (multipart) — don't set Content-Type; browser sets boundary
  async upload<T>(path: string, formData: FormData): Promise<T> {
    const epoch = captureStaffEpoch();
    assertStaffWriteAvailable(path);
    const token = await getAccessToken();
    assertStaffEpoch(epoch);
    assertStaffWriteAvailable(path);
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
    assertStaffEpoch(epoch);
    responseEpochs.set(response, epoch);
    if (!response.ok) {
      throw await failure(response, "POST", path);
    }
    return readJson<T>(response);
  },

  // Download a file and trigger browser save dialog
  async download(path: string, fallbackFilename = "download", expectedContentType?: string): Promise<void> {
    const epoch = captureStaffEpoch();
    const token = await getAccessToken();
    assertStaffEpoch(epoch);
    const response = await fetch(`${API_BASE_URL}${path}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new ApiError(response.status, `Download ${path} failed: ${response.status}`, null);
    }
    if (expectedContentType && response.headers.get("Content-Type")?.split(";")[0]?.trim().toLowerCase() !== expectedContentType.toLowerCase()) {
      throw new Error("The download returned an unexpected file type.");
    }
    const blob = await response.blob();
    assertStaffEpoch(epoch);
    const disposition = response.headers.get("Content-Disposition");
    const filename = disposition?.match(/filename="?([^";\n]+)"?/)?.[1] ?? fallbackFilename;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
